// src/utils/handleApiError.js
import { removeLocalStorageItems } from "./removeLocalStorageUtility";
import { useMsal } from "@azure/msal-react";

/**
 * Safely converts error data to a string for comparison operations.
 * Handles cases where data could be a string, object, or undefined.
 * @param {any} data - The error data to normalize
 * @returns {string} - A string representation of the data
 */
function normalizeErrorData(data) {
    if (data === null || data === undefined) {
        return '';
    }
    if (typeof data === 'string') {
        return data;
    }
    if (typeof data === 'object') {
        return data.Message || data.ExceptionMessage || data.message || JSON.stringify(data);
    }
    return String(data);
}

/**
 * Extracts a user-friendly error message from the exception.
 * @param {object} exception - The exception object
 * @returns {string} - A user-friendly error message
 */
function extractErrorMessage(exception) {
    if (!exception) return 'An unexpected error occurred';
    
    const data = exception.data;
    if (data === null || data === undefined) {
        return `Error ${exception.status || 'Unknown'}`;
    }
    
    if (typeof data === 'string') {
        return data;
    }
    
    if (typeof data === 'object') {
        return data.Message || data.ExceptionMessage || data.message || data.error || JSON.stringify(data);
    }
    
    return String(data);
}

/**
 * Common API error handler for React apps.
 * @returns {function} handleApiError - The error handler function.
 */
export function useHandleApiError() {
    const { instance } = useMsal();
    return (exception, {
        setErrorMessage,
        setErrorDetail,
        setLogoutOnClose,
    } = {}) => {
        const unAuthorizedStatusText = "Unauthorized Session";
        const shva = localStorage.getItem('shva');
        const canPerformLogout = shva && shva !== 'undefined';
        
        const errorDataStr = normalizeErrorData(exception?.data);
        const errorDataLower = errorDataStr.toLowerCase().trim();

        if (exception?.data === 'User login is required|login required') return false;
        if (exception?.status === -1) return;
        
        if (exception?.status === 401 && errorDataLower === unAuthorizedStatusText.toLowerCase().trim()) {
            setErrorMessage && setErrorMessage("Your session has been disconnected.");
            setLogoutOnClose && setLogoutOnClose(true);
            return;
            /* alert("Your session has been disconnected.");
            if (canPerformLogout) {
                try {
                    removeLocalStorageItems();
                    sessionStorage.clear();
                    if (instance) {
                        instance.logoutRedirect();
                    } else {
                        window.location.reload();
                    }
                } catch (error) {
                    console.error("Logout error:", error);
                }
            }
            return false; */
        }
        
        if (exception?.status === 401) {
            console.error(errorDataLower);
            setErrorMessage && setErrorMessage("You don't have permissions to view this page.");
            return;
        }

        if (exception?.status === 400) {
            if (errorDataLower.includes("please contact administrator") ||
                errorDataLower.includes("please contact your system administrator")) {
                setErrorMessage && setErrorMessage(errorDataStr);
                setLogoutOnClose && setLogoutOnClose(true);
                return;
            }
            if (errorDataLower.includes("user is already login")) {
                //setErrorMessage && setErrorMessage("User is already logged in from another session.");
                return;
            }
            setErrorMessage && setErrorMessage(errorDataStr || "Bad request");
            setErrorDetail && setErrorDetail(JSON.stringify(exception.data, null, 2));
            return;
        }

        if (exception?.status === 306) {
            setErrorMessage && setErrorMessage("Your session has been disconnected.");
            setLogoutOnClose && setLogoutOnClose(true);
            return;
            /* alert("Your session has been disconnected.");
            if (canPerformLogout) {
                try {
                    removeLocalStorageItems();
                    sessionStorage.clear();
                    if (instance) {
                        instance.logoutRedirect();
                    } else {
                        window.location.reload();
                    }
                } catch (error) {
                    console.error("Logout error:", error);
                }
            }
            return false; */
        }

        if (errorDataStr.includes("Token renewal operation failed due to timeout") ||
            errorDataStr.includes("A silent sign-in request was sent")) {
            if (canPerformLogout) {
                removeLocalStorageItems();
                sessionStorage.clear();
                if (instance) {
                    instance.logoutRedirect();
                } else {
                    window.location.reload();
                }
            }
            return;
        }

        if (exception?.status === 409) {
            const conflictMessage = extractErrorMessage(exception);
            setErrorMessage && setErrorMessage(conflictMessage || "Request could not be completed due to a conflict.");
            setErrorDetail && setErrorDetail(JSON.stringify(exception.data, null, 2));
            return;
        }

        if (exception?.status === 500) {
            const serverErrorMessage = extractErrorMessage(exception);
            setErrorMessage && setErrorMessage("Server error: " + (serverErrorMessage || "An internal server error occurred."));
            setErrorDetail && setErrorDetail(JSON.stringify(exception.data, null, 2));
            return;
        }

        let errorDetail = "";
        if (exception?.data !== undefined) {
            errorDetail = JSON.stringify(exception.data, null, 2);
        }
        setErrorMessage && setErrorMessage(extractErrorMessage(exception) || "Unexpected error");
        setErrorDetail && setErrorDetail(errorDetail);
    };
}
